<template>
    <li>
        <div class="lam-page-graph">
            <div class="uk-button-group lam-graph-filter uk-text-center">
                <base-button
                    size="mini"
                    :class="{'uk-active': chart_scope==='quarter'}"
                    @click.prevent="chart_scope='quarter'"
                >
                    12 Weeks
                </base-button>
                <base-button
                    size="mini"
                    :class="{'uk-active': chart_scope==='remaining'}"
                    @click.prevent="chart_scope='remaining'"
                >
                    Remaining
                </base-button>
                <base-button
                    size="mini"
                    :class="{'uk-active': chart_scope==='project'}"
                    @click.prevent="chart_scope='project'"
                >
                    Whole Project
                </base-button>
            </div>
            <div class="uk-grid uk-grid uk-margin-bottom">
                <div class="uk-width-1-2">
                    <h4 class="uk-text-center uk-margin-bottom-remove">Fee Spent</h4>
                    <div>
                        <projected-fee-graph
                            :chart-data="projected_fee_chart_data"
                            :options="projected_fee_graph_options"
                            style="height: 400px;"
                        />
                    </div>
                </div>
                <div class="uk-width-1-2">
                    <h4 class="uk-text-center uk-margin-bottom-remove">Projected Hours</h4>
                    <div>
                        <projected-hours-graph
                            :chart-data="projected_hours_chart_data"
                            style="height: 400px;"
                        />
                    </div>
                </div>
            </div>

            <div v-if="editable && !!record && !!record.computed_starts_at" class="uk-text-right">
                <base-button
                    size="small"
                    class="uk-margin-bottom"
                    @click.prevent="offsetting = true"
                >
                    <open-icon glyph="fast-forward" />
                </base-button>
            </div>

            <div
                :class="{'lam-wps-dragging': is_dragging}"
                class="lam-wps"
            >
                <phase
                    v-for="phase in ordered_phase"
                    :key="phase.id"
                    :record="phase"
                    :project="record"
                    :editable="editable"
                    :dragging="is_dragging"
                    @drag="is_dragging = $event"
                    @show-comments="show_phase_comments"
                />
            </div>
            <base-button
                v-if="editable"
                color="secondary"
                class="uk-width-1-1 uk-margin-large-top"
                @click.prevent="adding_phases=true"
            >
                Add new phase(s)
            </base-button>

            <project-phases-wizard
                v-if="!!record"
                :show.sync="adding_phases"
                :record="record"
            />

            <modal-dialog
                id="pick-team-member"
                :show.sync="picking_new_team_member"
            >
                <template v-slot:title>
                    Add a new staff member to the project
                </template>

                <form class="uk-form">
                    <div class="uk-grid uk-grid-small">
                        <div class="uk-width-1-1">
                            <form-select-user
                                id="new-team-member-user_id"
                                name="user_id"
                                v-model="new_team_member.user_id"
                                :empty-value="0"
                                empty-label="Select a staff member..."
                                :filter="new_team_member_filter"
                                :editable="true"
                                :required="true"
                                class="uk-width-1-1"
                            />
                        </div>
                    </div>
                    <div v-if="available_team_member_capacities.length > 1" class="uk-grid uk-grid-small">
                        <div class="uk-width-1-1">
                            <form-select
                                id="new-team-member-capacity"
                                name="capacity"
                                v-model="new_team_member.capacity"
                                :options="available_team_member_capacities"
                                :editable="true"
                                :required="true"
                                class="uk-width-1-1"
                            />
                        </div>
                    </div>

                    <div class="uk-modal-footer uk-text-center">
                        <div class="uk-grid">
                            <div class="uk-width-1-2">
                                <base-button
                                    size="large"
                                    width="1-1"
                                    class="uk-width-1-1"
                                    @click.prevent="picking_new_team_member=false"
                                >
                                    Cancel
                                </base-button>
                            </div>
                            <div class="uk-width-1-2">
                                <base-button
                                    size="large"
                                    color="primary"
                                    width="1-1"
                                    class="uk-width-1-1"
                                    :disabled="!new_team_member.user_id"
                                    @click.prevent="add_team_member"
                                >
                                    Add
                                </base-button>
                            </div>
                        </div>
                    </div>
                </form>
            </modal-dialog>

            <offset-dialog
                v-if="!!record"
                :show.sync="offsetting"
                :record="record"
            />
            <comments-dialog
                v-if="!!record"
                :show.sync="displaying_phase_comments"
                :title="`Comments for ${phase_standardized_title(displayed_phase)}`"
                :comments="displayed_phase_comments"
                :owner-id="record.client_id || 0"
                owner-type="App.Firm"
                :editable="false"
            />
        </div>
    </li>
</template>

<script>
import { orderBy } from "lodash"
import { draw } from "patternomaly"
import numeral from "numeral"

import tab from "../mixins/Tab"
import profile_utilities from "../../../mixins/ProfileUtilities"
import project_utilities from "../../../mixins/ProjectUtilities"
import phase_utilities from "../../../mixins/PhaseUtilities"
import handles_phase_builder_selection from "../../../mixins/HandlesPhaseBuilderSelection"
import uses_color_palette from "../../../mixins/UsesColorPalette"

import ModalDialog from "../../../dialogs/ModalDialog/ModalDialog"
import FormSelect from "../../../ui/FormSelect"
import FormSelectUser from "../../../ui/FormSelectUser"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import Phase from "./Phase"
import ProjectedHoursGraph from "./ProjectedHoursGraph.js"
import ProjectedFeeGraph from "./ProjectedFeeGraph.js"
import ProjectPhasesWizard from "../../../dialogs/ProjectPhasesWizard"
import CommentsDialog from "../../../dialogs/CommentsDialog"
import OffsetDialog from "../../../dialogs/OffsetDialog"

export default {
    mixins: [tab, profile_utilities, project_utilities, phase_utilities, handles_phase_builder_selection, uses_color_palette],
    components: {
        ModalDialog,
        FormSelect,
        FormSelectUser,
        BaseButton,
        OpenIcon,
        Phase,
        ProjectedHoursGraph,
        ProjectedFeeGraph,
        ProjectPhasesWizard,
        CommentsDialog,
        OffsetDialog
    },
    mounted () {
        this.reload()
    },
    watch: {
        $route: "reload",
        "record.id": "reload",
        profile_id: "reload"
    },
    methods: {
        reload () {
            if(this.profile_id && this.record && this.record.id) {
                this.$store.dispatch(
                    "FETCH_RECORDS",
                    {
                        entity: "task_user",
                        query: {
                            project_id: this.record.id,
                            relation_ids: ["subtask", "subtask_user"]
                        }
                    }
                ).then(() => {
                }).catch(error => {
                    this.$error(error.message)
                })
            }
        },
        new_team_member_filter (user) {
            return (this.record.pm_id !== user.id) && (this.record.pic_id !== user.id) && (this.record.designer_ids.indexOf(user.id) < 0)
        },
        pick_new_team_member () {
            // IF pic and pm assigned, add as designer
            // else let user pick from available capacities
            const available_team_members = this.available_team_members
            const available_team_member_capacities = this.available_team_member_capacities
            if(available_team_members.length) {
                this.new_team_member.project_id = this.record.id
                this.new_team_member.capacity = available_team_member_capacities[0].id
                if((available_team_members.length === 1) && (available_team_member_capacities.length === 1)) {
                    this.add_team_member()
                } else {
                    this.new_team_member.user_id = 0
                    this.picking_new_team_member = true
                }
            }
        },
        add_team_member () {
            // capacity = id
            this.$store.dispatch(
                "CREATE_RECORD",
                { entity: "project_user", data: this.new_team_member }
            ).then(() => {
                this.picking_new_team_member = false
            }).catch(error => {
                this.$error(error.message)
            })
        },
        fee_spent_for_date (date) {
            let total = 0
            this.phases.forEach(phase => {
                if(phase.computed_spent_history) {
                    let phase_total = 0
                    Object.keys(phase.computed_spent_history).forEach(financial_date_string => {
                        const financial_date = this.$moment(financial_date_string)
                        if(financial_date.isSameOrBefore(date)) phase_total = parseFloat(phase.computed_spent_history[financial_date_string].spent)
                    })
                    total += phase_total
                }
            })
            return total
        },
        fee_projected_for_range (starts_at, ends_at, ignore_completed = false) {
            let total = 0

            const task_projection = (task) => {
                let total = 0
                if(task.working_weeks) {
                    Object.keys(task.working_weeks).forEach(initials => {
                        const working_weeks = task.working_weeks[initials]
                        const user = this.raw_user_records.find(user => user.initials === initials)
                        if(user && user.billing_rate) {
                            const hours = this.working_hours(working_weeks, starts_at, ends_at)
                            const projection = Math.round(user.billing_rate * hours)
                            total += projection
                        }
                    })
                }
                return total
            }

            this.tasks.filter(milestone => {
                return !milestone.milestone_id && (!ignore_completed || (ignore_completed && !milestone.completed_at))
            }).forEach(milestone => {
                const tasks = this.tasks.filter(task => {
                    return (task.milestone_id === milestone.id) && (!ignore_completed || (ignore_completed && !task.completed_at))
                })
                if(!!tasks && !!tasks.length) {
                    tasks.forEach(task => {
                        total += task_projection(task)
                    })
                } else total += task_projection(milestone)
            })

            return total
        },
        starts_at (field = "computed_starts_at") {
            let min = null
            this.phases.forEach((phase) => {
                const starts_at = this.$moment(phase[field])
                if(starts_at) {
                    if(!min) min = starts_at
                    else if(starts_at.isBefore(min)) min = starts_at
                }
            })
            if(!min) min = this.ends_at
            return min
        },
        show_phase_comments (phase, comments) {
            this.displayed_phase = phase
            this.displayed_phase_comments = comments
            this.displaying_phase_comments = true
        }
    },
    computed: {
        phases () {
            if(!this.record || !this.record.id) return []
            return this.raw_phase_records.filter(phase => phase.project_id === this.record.id)
        },
        ordered_phase () {
            return orderBy(this.phases, ["deltek_id", "computed_starts_at"], ["asc", "asc"])
        },
        tasks () {
            if(!this.record || !this.record.id) return []
            return this.raw_task_records.filter(task => task.project_id === this.record.id)
        },
        projected_hours_chart_data () {
            const data = { labels: [], datasets: {} }
            const users_for_project = orderBy(this.users_for_project_id(this.record.id), "initials", "asc")
            users_for_project.forEach(user => {
                data.datasets[user.initials] = {
                    label: user.initials,
                    backgroundColor: [],
                    labelColor: user.hex_color,
                    categoryPercentage: 0.9,
                    barPercentage: 0.8,
                    data: [],
                    _hex_color: user.hex_color,
                    _off_data: []
                }
            })

            const inflate_data = (date, end, label_format = "MMM Do", end_of = "isoWeek", add = 1, add_units = "weeks") => {
                let data_index = -1

                while(date.isSameOrBefore(end)) {
                    data_index++

                    const next_date = this.$moment(date).add(add, add_units)
                    let label = date.format(label_format)
                    if(!data_index || (next_date.isAfter(end)) || (date.week() === 1)) label = date.format(`${label_format} YYYY`)
                    data.labels.push(label)

                    Object.keys(data.datasets).forEach(initials => {
                        const dataset = data.datasets[initials]
                        dataset.data.push(null)
                        dataset._off_data.push(null)
                    })

                    const to = this.$moment(date).add(add, add_units).subtract(1, "day").endOf(end_of)
                    this.tasks.filter(task => {
                        const starts_at = task.starts_at || task.proxied_starts_at
                        if(!!task.completed_at || !!this.tasks.find(child => child.milestone_id === task.id) || !starts_at) return false

                        const task_starts = this.$moment(starts_at).startOf("day")
                        const task_ends = this.$moment(task.ends_at || task.proxied_ends_at).endOf("day")

                        if(date.isSameOrAfter(task_starts) && to.isSameOrBefore(task_ends)) return true
                        if(task_starts.isSameOrAfter(date) && task_starts.endOf("day").isSameOrBefore(to)) return true
                        if(task_ends.isSameOrBefore(to) && task_ends.startOf("day").isSameOrAfter(date)) return true

                        return false
                    }).forEach(milestone => {
                        const memory = []
                        users_for_project.forEach(user => {
                            if(milestone.working_weeks && milestone.working_weeks[user.initials] && (memory.indexOf(user.initials) < 0)) {
                                memory.push(user.initials)
                                data.datasets[user.initials].data[data_index] += this.working_hours(milestone.working_weeks[user.initials], date, to)
                            }
                        })
                    })

                    users_for_project.forEach(user => {
                        const days_off = this.off_days(user, date, to)
                        data.datasets[user.initials]._off_data[data_index] = (days_off * this.$root.applicationSettings.work_day_hours) || null

                        if(data.datasets[user.initials]._off_data[data_index]) data.datasets[user.initials].backgroundColor.push(draw("diagonal", user.hex_color, window.Color(user.hex_color).lighten(0.5).rgbString(), 15))
                        else data.datasets[user.initials].backgroundColor.push(user.hex_color)
                    })

                    date.add(add, add_units)
                }
            }

            const ends_at_moment = this.$moment(this.ends_at).endOf("isoWeek").endOf("day")
            switch (this.chart_scope) {
            case "quarter": // 12 weeks out, as of this week
                {
                    const end = this.$moment().endOf("day").add(12, "weeks")
                    inflate_data(this.$moment().startOf("isoWeek"), end)
                }
                break

            case "remaining": // everything left as of today
                if(this.$moment(ends_at_moment).diff(this.$moment(), "weeks") > 12) inflate_data(this.$moment().startOf("isoWeek"), ends_at_moment)
                else inflate_data(this.$moment().startOf("isoWeek"), ends_at_moment)
                break

            default: // project: the whole project from start to finish
                inflate_data(this.$moment(this.starts_at()).startOf("isoWeek"), ends_at_moment)
                break
            }

            data.datasets = Object.values(data.datasets)

            return data
        },
        team_member_capacities () {
            return this.dictionary("project_user_capacity")
        },
        available_team_member_capacities () {
            return this.team_member_capacities.filter(capacity => {
                if(capacity.code_constant === "PIC") return false
                if((capacity.code_constant === "PM") && !!this.record.pm_id) return false
                return true
            })
        },
        available_team_members () {
            if(!this.record || !this.record.id) return []
            return this.raw_user_records.filter(this.new_team_member_filter)
        },
        ends_at () {
            let max = null
            this.phases.forEach((phase) => {
                const ends_at = this.$moment(phase.computed_ends_at)
                if(ends_at) {
                    if(!max) max = ends_at
                    else if(ends_at.isAfter(max)) max = ends_at
                }
            })
            return max
        },
        projected_fee_chart_data () {
            if(!this.tasks) return null

            const data = {
                labels: [],
                datasets: [
                    {
                        label: "Actual",
                        backgroundColor: this.color_languished.background,
                        borderColor: this.color_languished.border,
                        borderWidth: 2,
                        pointRadius: [],
                        pointHoverRadius: 4,
                        cubicInterpolationMode: "monotone",
                        fill: false,
                        data: []
                    },
                    /*
                {
                    label: "Projected",
                    backgroundColor: this.color_active.background,
                    borderColor: this.color_active.border,
                    borderWidth: 2,
                    pointRadius: [],
                    pointHoverRadius: 4,
                    cubicInterpolationMode: "monotone",
                    fill: false,
                    data: []
                },
                {
                    label: "Actual + Projected",
                    backgroundColor: this.color_active.background,
                    borderColor: this.color_active.border,
                    borderDash: [5, 5],
                    borderWidth: 2,
                    pointRadius: [],
                    pointHoverRadius: 4,
                    cubicInterpolationMode: "monotone",
                    fill: false,
                    data: []
                }
                */
                    {
                        label: "Projected",
                        backgroundColor: this.color_active.background,
                        borderColor: this.color_active.border,
                        borderDash: [5, 5],
                        borderWidth: 2,
                        pointRadius: [],
                        pointHoverRadius: 4,
                        cubicInterpolationMode: "monotone",
                        fill: false,
                        data: []
                    }
                ]
            }
            const inflate_data = (date, end, label_format = "MMM Do", end_of = "isoWeek", add = 1, add_units = "weeks") => {
                let fee_spent = 0
                let projected = 0
                const current_week_start = this.$moment().startOf("isoWeek")
                const current_week_end = this.$moment(current_week_start).add(1, "weeks")
                let from = this.$moment(this.starts_at()).startOf("day") // **** FEE SPENT graph fix
                const to = this.$moment(this.ends_at).endOf("day")

                while(date.isBefore(end)) {
                    data.labels.push(date.format(label_format))
                    if(date.isSameOrBefore(to)) {
                        const date_is_current_week = date.isBetween(current_week_start, current_week_end, null, "[)")

                        const end_date = this.$moment(date).endOf("isoWeek")
                        if(date.isBefore(current_week_end)) {
                            from = this.$moment(date).add(add, add_units)
                            fee_spent = this.fee_spent_for_date(end_date)
                            // projected = this.fee_projected_for_range(from, end_date)
                            // Actual + Projected
                            data.datasets[1].data.push(date_is_current_week ? fee_spent : null)
                            data.datasets[1].pointRadius.push(0)
                            // Actual
                            data.datasets[0].data.push(fee_spent)
                            data.datasets[0].pointRadius.push(date_is_current_week ? 4 : 0)
                            // Projected
                            /*
                            data.datasets[1].data.push(projected)
                            data.datasets[1].pointRadius.push(date_is_current_week ? 4 : 0)
                            */
                        } else {
                            projected = this.fee_projected_for_range(from, end_date, false) // do NOT ignore completed tasks in projection here...
                            // Actual + Projected
                            data.datasets[1].data.push(fee_spent + projected)
                            data.datasets[1].pointRadius.push(date_is_current_week ? 4 : 0)
                            // Actual
                            data.datasets[0].data.push(null)
                            data.datasets[0].pointRadius.push(date_is_current_week ? 4 : 0)
                            // Projected
                            /*
                            data.datasets[1].data.push(null)
                            data.datasets[1].pointRadius.push(date_is_current_week ? 4 : 0)
                            */
                        }
                    }

                    date.add(add, add_units)
                }
            }

            let start
            const ends_at_moment = this.$moment(this.ends_at).endOf("isoWeek")
            switch (this.chart_scope) {
            case "quarter": // 16 weeks out, as of 4 weeks ago
                {
                    start = this.$moment().startOf("isoWeek").subtract(4, "weeks")
                    const end = this.$moment(start).endOf("day").add(16, "weeks")
                    // if(end.isAfter(ends_at_moment)) end = ends_at_moment
                    inflate_data(start, end)
                }
                break

            case "remaining": // everything left as of today
                start = this.$moment().startOf("isoWeek")
                // if(this.$moment(ends_at_moment).diff(this.$moment(), "weeks") > 12) start = this.$moment().startOf('isoWeek')

                inflate_data(start.subtract(4, "weeks"), ends_at_moment)
                break

            default: // project: the whole project from start to finish
                inflate_data(this.$moment(this.starts_at()).startOf("isoWeek"), ends_at_moment)
                break
            }

            return data
        },
        projected_fee_graph_options () {
            const annotations = []

            const total_fee = this.record.computed_fee
            // const last_phase_index = this.phases.length - 1
            let total = 0
            orderBy(this.phases, ["computed_starts_at", "computed_ends_at"], "asc").forEach((phase, index) => {
                const phase_info = this.phase_by_id(phase.phase)
                total += phase.fee
                annotations.push({
                    type: "line",
                    mode: "horizontal",
                    scaleID: "y-axis-0",
                    value: total,
                    borderColor: this.color_on_hold.border,
                    borderWidth: 1,
                    label: {
                        backgroundColor: "transparent",
                        fontFamily: "'Helvetica Neue', Helvetica, Arial, 'sans-serif'",
                        fontSize: 10,
                        fontStyle: "bold",
                        fontColor: this.color_on_hold.border,
                        position: "left",
                        xPadding: 3,
                        yPadding: 3,
                        xAdjust: 0,
                        yAdjust: 8, // (index >= last_phase_index) ? 6 : 0,
                        cornerRadius: 0,
                        enabled: true,
                        content: phase_info.code_constant + " $ " + this.nibnut_filter("nibnut.number", [phase.fee, "0,0"])
                    }
                })
            })
            annotations.push({
                type: "line",
                mode: "horizontal",
                scaleID: "y-axis-0",
                value: total_fee,
                borderColor: (total === total_fee) ? "transparent" : this.color_on_hold.border,
                borderWidth: 1,
                label: {
                    backgroundColor: "transparent",
                    fontFamily: "'Helvetica Neue', Helvetica, Arial, 'sans-serif'",
                    fontSize: 10,
                    fontStyle: "bold",
                    fontColor: this.color_on_hold.border,
                    position: "right",
                    xPadding: 3,
                    yPadding: 3,
                    xAdjust: 0,
                    yAdjust: 8,
                    cornerRadius: 0,
                    enabled: true,
                    content: "Fee $ " + this.nibnut_filter("nibnut.number", [total_fee, "0,0"])
                }
            })

            return {
                tooltips: {
                    callbacks: {
                        label: function (tooltipItem, data) {
                            return "$" + numeral(tooltipItem.yLabel).format("0,0")
                        }
                    }
                },
                scales: {
                    yAxes: [
                        {
                            ticks: {
                                beginAtZero: true,
                                suggestedMax: Math.max(total, total_fee),
                                fontColor: "#00274F",
                                callback: function (value, index, values) {
                                    return "$" + numeral(value).format("0,0")
                                }
                            },
                            gridLines: {
                                display: true,
                                color: "rgba(0, 39, 79, 0.1)"
                            }
                        }
                    ],
                    xAxes: [{
                        gridLines: {
                            display: false,
                            color: "rgba(0, 39, 79, 0.1)"
                        }
                    }]
                },
                legend: {
                    display: true,
                    labels: {
                        fontColor: "#00274F",
                        filter: (legendItem, chartData) => {
                            return legendItem.datasetIndex <= 1 // (!!this.record.data_source ? 1 : 2) // (!!this.record.data_source ? 0 : 1)
                        }
                    }
                },
                responsive: true,
                maintainAspectRatio: false,
                annotation: {
                    drawTime: "afterDatasetsDraw",
                    annotations
                }
            }
        }
    },
    data () {
        return {
            chart_scope: "quarter", // "quarter", "remaining" or "project"
            is_dragging: false,

            new_team_member: {
                project_id: 0,
                user_id: 0,
                capacity: 0
            },
            picking_new_team_member: false,

            offsetting: false,

            displaying_phase_comments: false,
            displayed_phase: {},
            displayed_phase_comments: []
        }
    }
}
</script>

<style lang="less">
    canvas {
        margin-left: auto;
        margin-right: auto;
    }
</style>
